import { useMemo, useState } from "react";
import { Eye, EyeOff, Globe, Loader2, MapPin, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function PromotionsTab({
  loading,
  promotions,
  locations = [],
  onEditPromotion,
  onCreatePromotion,
  onDeletePromotion,
}) {
  const [targetFilter, setTargetFilter] = useState("all");

  const locationLabel = (slug) => locations.find((l) => l.slug === slug)?.name || slug;

  const filteredPromotions = useMemo(() => {
    if (targetFilter === "all") return promotions;
    if (targetFilter === "public") return promotions.filter((p) => p.is_public);
    return promotions.filter((p) => (p.available_locations || []).includes(targetFilter));
  }, [promotions, targetFilter]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#1aa59a]" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Button
        onClick={onCreatePromotion}
        className="bg-[#1aa59a] hover:bg-[#158a80] w-full"
        data-testid="add-promotion-button"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Promotion
      </Button>

      <Select value={targetFilter} onValueChange={setTargetFilter}>
        <SelectTrigger className="w-full sm:w-72" data-testid="promotions-target-filter">
          <SelectValue placeholder="All promotions" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All promotions</SelectItem>
          <SelectItem value="public">🌐 Main site (public)</SelectItem>
          {locations.map((loc) => (
            <SelectItem key={loc.slug} value={loc.slug}>
              📍 {loc.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {filteredPromotions.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          No promotions match this filter.
        </div>
      ) : (
        <div className="space-y-2">
          {filteredPromotions.map((promo) => {
            const targets = [];
            if (promo.is_public) targets.push({ label: "Public", icon: <Globe className="w-3 h-3" /> });
            (promo.available_locations || []).forEach((slug) => {
              targets.push({ label: locationLabel(slug), icon: <MapPin className="w-3 h-3" /> });
            });

            return (
              <Card
                key={promo.id}
                className={`overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${
                  !promo.is_active ? "opacity-60" : ""
                }`}
                onClick={() => onEditPromotion(promo)}
                data-testid={`promo-card-${promo.id}`}
              >
                <div className="flex items-center p-3 gap-3">
                  <div className="w-20 h-12 bg-gradient-to-r from-[#1aa59a] to-[#158a80] rounded-lg overflow-hidden flex-shrink-0">
                    {promo.image_base64 ? (
                      <img
                        src={promo.image_base64}
                        alt={promo.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white text-xs">
                        Slot {promo.slot}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm truncate">
                      {promo.title || `Promotion ${promo.slot}`}
                    </h3>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {targets.length === 0 ? (
                        <span className="text-xs text-red-500 italic">No targets selected</span>
                      ) : (
                        targets.map((t, idx) => (
                          <span
                            key={`${promo.id}-target-${idx}`}
                            className="inline-flex items-center gap-1 text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded"
                          >
                            {t.icon}
                            {t.label}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0 flex items-center gap-2">
                    {promo.is_active ? (
                      <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Eye className="w-3 h-3" /> On
                      </span>
                    ) : (
                      <span className="bg-gray-100 text-gray-500 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <EyeOff className="w-3 h-3" /> Off
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeletePromotion(promo);
                      }}
                      className="text-red-500 p-1.5 rounded hover:bg-red-50 transition-colors"
                      data-testid={`delete-promo-${promo.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
